import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const donutProgressVariants = cva("", {
  variants: {
    variant: {
      default: "",
      success: "",
      warning: "",
      danger: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface DonutProgressProps
  extends React.SVGAttributes<SVGSVGElement>,
    VariantProps<typeof donutProgressVariants> {
  value: number; // 0-100
  size?: number; // Size in pixels
  strokeWidth?: number; // Thickness of the donut
  showLabel?: boolean; // Show percentage in center
  showValue?: boolean; // Show value instead of percentage
}

export const DonutProgress = React.forwardRef<
  SVGSVGElement,
  DonutProgressProps
>(
  (
    {
      value,
      size = 40,
      strokeWidth = 4,
      className,
      showLabel = false,
      showValue = false,
      variant,
      ...props
    },
    ref,
  ) => {
    const normalizedValue = Math.min(100, Math.max(0, value));
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset =
      circumference - (normalizedValue / 100) * circumference;

    // Determine stroke color based on completion percentage
    const getProgressStrokeColor = () => {
      if (normalizedValue === 100) return "hsl(var(--supporting-green))";
      if (normalizedValue >= 75) return "hsl(var(--supporting-blue))";
      if (normalizedValue >= 50) return "hsl(var(--supporting-yellow))";
      if (normalizedValue >= 25) return "hsl(var(--supporting-orange))";
      return "hsl(var(--supporting-red))";
    };

    const progressStrokeColor = getProgressStrokeColor();

    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        className={cn(
          "transform -rotate-90",
          donutProgressVariants({ variant }),
          className,
        )}
        aria-valuenow={normalizedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        role="progressbar"
        {...props}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={strokeWidth}
          opacity={0.3}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressStrokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-300 ease-in-out"
        />
        {showLabel && (
          <g transform={`rotate(90 ${size / 2} ${size / 2})`}>
            <text
              x="50%"
              y="50%"
              fill="hsl(var(--foreground))"
              fontSize={Math.max(8, size * 0.22)}
              fontWeight="500"
              className="tabular-nums"
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {showValue ? value : `${Math.round(normalizedValue)}%`}
            </text>
          </g>
        )}
      </svg>
    );
  },
);

DonutProgress.displayName = "DonutProgress";
